/**
 * excursionApply — the reconcile leg (05-VERTICAL-SLICE §VS-3).
 *
 * Applies a human-reviewed ExcursionMergePlan. Uncertainty policy
 * (02-DESIGN §5): explicit / symbol / visual_language assertions write
 * edges directly (declared_mode 'emergent'); spatial / llm_inference
 * assertions become review proposals (Nexus lifecycle, 'excursion_return'
 * agent) — never silent writes. Removed objects are flagged, never deleted.
 *
 * §2.3a: none of these writes is an authority-lane transition (same posture
 * as connector credentials); direct service/store writes are appropriate.
 */

import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useAuditStore } from '../../stores/prioritization/auditStore';
import { createNexusProposal } from '../nexusAgentService';
import { registerExternalId, logChangeEvent } from '../connectors';
import { recordAndPropagateInbound } from './unifiedAttributesService';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';
import type { RelationshipEdgeType } from '../../types/prioritization/itemRelationships';
import type { ConnectorExcursion } from './excursionStore';
import { setExcursionStatus } from './excursionStore';
import { newImportBatchId } from '../importBatch';
import type { ExcursionMergePlan, MergedObject, MergedRelation } from './semanticExcursionMerge';

/** Which plan entries the reviewer approved. */
export interface ApplySelection {
  /** externalIds of new/edited/renamed objects to apply. */
  objectExternalIds: Set<string>;
  /** Indexes into plan.relations for new/retyped relations to apply. */
  relationIndexes: Set<number>;
}

export interface ApplyResult {
  itemsCreated: number;
  itemsUpdated: number;
  /** The import batch id stamped on every item this return created (items.import_batch_id). */
  importBatchId: string;
  edgesCreated: number;
  edgesRetyped: number;
  proposalsCreated: number;
  skipped: string[];
  errors: string[];
}

const DIRECT_WRITE_BASES = new Set(['explicit', 'symbol', 'visual_language']);

function proposalConfidence(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

function describeObject(merged: MergedObject): string {
  return merged.current?.content.text ?? merged.baseline?.content.text ?? merged.externalId ?? 'object';
}

export async function applyExcursionReturn(
  excursion: ConnectorExcursion,
  plan: ExcursionMergePlan,
  selection: ApplySelection,
  options: { userId?: string; importBatchId?: string } = {},
): Promise<ApplyResult> {
  const result: ApplyResult = {
    importBatchId: options.importBatchId ?? newImportBatchId(),
    itemsCreated: 0,
    itemsUpdated: 0,
    edgesCreated: 0,
    edgesRetyped: 0,
    proposalsCreated: 0,
    skipped: [],
    errors: [],
  };
  const workspaceId = excursion.workspaceId;
  const userId = options.userId;
  const itemsStore = useItemsStore.getState();
  const relationshipsStore = useItemRelationshipsStore.getState();
  const logItemImport = useAuditStore.getState().logItemImport;

  // Resolve every object's platform item id: matched objects carry it from
  // the baseline; approved new objects get one now.
  const internalByExternal = new Map<string, string>();
  for (const merged of plan.objects) {
    if (merged.externalId && merged.internalId) internalByExternal.set(merged.externalId, merged.internalId);
  }

  for (const merged of plan.objects) {
    if (!merged.externalId || !selection.objectExternalIds.has(merged.externalId)) continue;

    if (merged.status === 'new' && merged.current) {
      const itemId = crypto.randomUUID();
      const category = merged.current.asserted.itemCategory;
      const altitude = merged.current.asserted.altitudeLevel;
      try {
        itemsStore.createItem({
          id: itemId,
          title: merged.current.content.text,
          description: merged.current.content.description ?? '',
          source: 'Miro excursion',
          tags: ['miro-excursion'],
          // Authorship axes (migrations 604/605): machine-imported content,
          // explicitly approved by the reviewer in Return Review.
          creationSource: 'automated',
          creationReviewStatus: 'human_reviewed',
          importBatchId: result.importBatchId,
          ...(category ? { itemCategory: category.value as ItemLanguageType } : {}),
          ...(altitude ? { altitudeLevel: altitude.value } : {}),
        } as Parameters<typeof itemsStore.createItem>[0]);
        internalByExternal.set(merged.externalId, itemId);
        result.itemsCreated++;
        logItemImport({
          type: 'item_imported',
          itemId,
          source: 'miro_excursion',
          actorId: userId ?? 'unknown',
          actorRole: 'creator',
          note: `Born in Miro during excursion to “${excursion.externalContainerUrl ?? excursion.externalContainerId}” — ${
            category ? `category ${category.value} (${category.basis}, ${Math.round(category.confidence * 100)}%)` : 'no visual category'
          }.`,
          details: {
            excursionId: excursion.id,
            externalId: merged.externalId,
            boardUrl: excursion.externalContainerUrl,
          },
        });
        await registerExternalId({
          workspaceId,
          entityType: 'item',
          internalId: itemId,
          connectorType: 'miro',
          externalId: merged.externalId,
          externalUrl: excursion.externalContainerUrl
            ? `${excursion.externalContainerUrl}?moveToWidget=${merged.externalId}`
            : undefined,
        });
        await logChangeEvent({
          workspaceId,
          entityType: 'item',
          entityId: itemId,
          eventType: 'created',
          newValue: { title: merged.current.content.text, from: 'miro_excursion' },
        });
        // Context inflation: new items inherit the excursion's Collection.
        if (excursion.scope.collectionId) {
          try {
            await relationshipsStore.addGroupMembers(excursion.scope.collectionId, [itemId]);
          } catch {
            result.skipped.push(`Could not add “${merged.current.content.text}” to the excursion collection.`);
          }
        }
      } catch (err) {
        result.errors.push(`Create failed for “${describeObject(merged)}”: ${err instanceof Error ? err.message : 'unknown'}`);
      }
      continue;
    }

    if ((merged.status === 'renamed' || merged.status === 'edited') && merged.internalId && merged.current) {
      try {
        const patch: Record<string, unknown> = {};
        if (merged.status === 'renamed') patch.title = merged.current.content.text;
        if ((merged.current.content.description ?? '') !== (merged.baseline?.content.description ?? '')) {
          patch.description = merged.current.content.description ?? '';
        }
        const category = merged.current.asserted.itemCategory;
        if (category && category.value !== merged.baseline?.asserted.itemCategory?.value && DIRECT_WRITE_BASES.has(category.basis)) {
          patch.itemCategory = category.value;
        }
        if (Object.keys(patch).length === 0) {
          result.skipped.push(`No applicable field change for “${describeObject(merged)}”.`);
          continue;
        }
        itemsStore.updateItem(merged.internalId, patch);
        result.itemsUpdated++;
        await logChangeEvent({
          workspaceId,
          entityType: 'item',
          entityId: merged.internalId,
          eventType: 'updated',
          ...(merged.previousTitle ? { fieldName: 'title', oldValue: merged.previousTitle, newValue: merged.current.content.text } : {}),
        });
      } catch (err) {
        result.errors.push(`Update failed for “${describeObject(merged)}”: ${err instanceof Error ? err.message : 'unknown'}`);
      }
    }
    // 'moved' and 'unchanged' need no home-side write; 'removed' is flag-only.
  }

  // Relations — the uncertainty policy fork.
  const existingEdges = useItemRelationshipsStore.getState().edges;
  // Unified attributes (doc 07): every object that now corresponds to an item
  // — matched or approved-new — reports its dialect attributes at rest, and
  // bound values apply per policy. Objects the reviewer did not approve are
  // recorded but held (doc 07 §5 Q5: the review's diff supersedes).
  {
    const objects: Array<{ object: Parameters<typeof recordAndPropagateInbound>[0]['objects'][number]['object']; itemId: string }> = [];
    const hold = new Set<string>();
    for (const merged of plan.objects) {
      if (!merged.externalId || !merged.current) continue;
      const itemId = internalByExternal.get(merged.externalId);
      if (!itemId) continue;
      objects.push({ object: merged.current, itemId });
      if (!selection.objectExternalIds.has(merged.externalId)) hold.add(itemId);
    }
    if (objects.length > 0) {
      const crossing = await recordAndPropagateInbound({
        workspaceId,
        connectorType: excursion.connectorType,
        origin: 'excursion_return',
        objects,
        holdItemIds: hold,
        userId,
      });
      result.errors.push(...crossing.errors);
      result.itemsUpdated += crossing.applied;
    }
  }

  for (const [index, merged] of plan.relations.entries()) {
    if (!selection.relationIndexes.has(index)) continue;
    if (merged.status !== 'new' && merged.status !== 'retyped') continue;
    const rel = merged.relation;
    if (!rel) continue;
    const sourceId = merged.sourceInternalId ?? internalByExternal.get(rel.sourceExternalId);
    const targetId = merged.targetInternalId ?? internalByExternal.get(rel.targetExternalId);
    if (!sourceId || !targetId || sourceId === targetId) {
      result.skipped.push(`Relation ${rel.canonicalType} (${rel.cue ?? 'no cue'}) — endpoint has no platform item.`);
      continue;
    }

    if (!DIRECT_WRITE_BASES.has(rel.assertion.basis)) {
      // Spatial / model inference → reviewable proposal, never a silent edge.
      const { error } = await createNexusProposal({
        workspaceId,
        sourceItemId: sourceId,
        targetItemId: targetId,
        relationshipType: rel.canonicalType,
        relationshipSubtype: rel.subtype,
        directionality: rel.directionality,
        rationale: `Inferred on Miro excursion return (${rel.assertion.basis}, ${Math.round(rel.assertion.confidence * 100)}%): ${rel.cue ?? 'layout'}.`,
        confidence: proposalConfidence(rel.assertion.confidence),
        proposedByAgent: 'excursion_return',
      });
      if (error) result.errors.push(`Proposal failed (${rel.canonicalType}): ${error}`);
      else result.proposalsCreated++;
      continue;
    }

    try {
      if (merged.status === 'retyped') {
        const previous = existingEdges.find(
          e =>
            e.type === (merged.previousType as RelationshipEdgeType) &&
            ((e.sourceItemId === sourceId && e.targetItemId === targetId) ||
              (rel.directionality === 'undirected' && e.sourceItemId === targetId && e.targetItemId === sourceId)),
        );
        if (previous) {
          const ok = await relationshipsStore.updateEdge(previous.id, {
            type: rel.canonicalType,
            ...(rel.subtype ? { subtype: rel.subtype as never } : {}),
            rationale: `Retyped on Miro excursion return (${rel.cue ?? 'board edit'}).`,
          });
          if (ok) {
            result.edgesRetyped++;
            // change_events entity_type has no relationship member — record
            // the retype against the source item (fieldName carries the fact).
            await logChangeEvent({
              workspaceId,
              entityType: 'item',
              entityId: sourceId,
              eventType: 'updated',
              fieldName: 'relationships',
              oldValue: { edgeId: previous.id, type: merged.previousType },
              newValue: { edgeId: previous.id, type: rel.canonicalType },
            });
            continue;
          }
        }
        // Fall through to create when the old edge can't be found.
      }
      const duplicate = existingEdges.some(
        e =>
          e.type === rel.canonicalType &&
          ((e.sourceItemId === sourceId && e.targetItemId === targetId) ||
            (rel.directionality === 'undirected' && e.sourceItemId === targetId && e.targetItemId === sourceId)),
      );
      if (duplicate) {
        result.skipped.push(`Edge ${rel.canonicalType} already exists between these items.`);
        continue;
      }
      const edge = await relationshipsStore.createEdge({
        type: rel.canonicalType,
        ...(rel.subtype ? { subtype: rel.subtype as never } : {}),
        directionality: rel.directionality,
        status: 'confirmed',
        declaredMode: 'emergent',
        sourceItemId: sourceId,
        targetItemId: targetId,
        rationale: `Drawn in Miro during excursion (${rel.cue ?? rel.assertion.basis}, ${Math.round(rel.assertion.confidence * 100)}%).`,
        metadata: { source: 'miro_excursion', excursionId: excursion.id, basis: rel.assertion.basis },
        ...(workspaceId ? { workspaceId } : {}),
        ...(userId ? { createdBy: userId } : {}),
      });
      if (edge) {
        result.edgesCreated++;
        await logChangeEvent({
          workspaceId,
          entityType: 'item',
          entityId: sourceId,
          eventType: 'updated',
          fieldName: 'relationships',
          newValue: { edgeId: edge.id, type: rel.canonicalType, target: targetId },
        });
      }
    } catch (err) {
      result.errors.push(`Edge write failed (${rel.canonicalType}): ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  await setExcursionStatus(excursion.id, 'reconciled');
  return result;
}

/** Removed-object flags for the review UI — never auto-applied. */
export function removedObjectFlags(plan: ExcursionMergePlan): string[] {
  return plan.objects
    .filter(o => o.status === 'removed')
    .map(o => `“${describeObject(o)}” was removed from the board — kept at home (review manually).`);
}

export function relationObservations(plan: ExcursionMergePlan): MergedRelation[] {
  return plan.relations.filter(r => (r.status === 'new' || r.status === 'retyped') && !r.applicable);
}
